import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button } from '@shared/components/Button';
import { Menu } from '@shared/components/Menu';
import { Modal } from '@shared/components/Modal';
import { AnchoredTooltip } from '@shared/components/tooltip';
import { getAnchorName, grid } from '@shared/styles';
import { useDisableGlobalHotkeys } from '@shared/util/hotkeys';
import { i18n } from '@shared/util/i18n';
import { capturePhotoFile } from './cameraPhoto';
import { CameraDevice, useCameraDevices } from './useCameraDevices';
import { CameraStatus, useCameraStream } from './useCameraStream';
import styles from './CameraCaptureModal.module.css';

/** The shutter's glyph, sized to the large button it sits in rather than to a row of small ones. */
const SHUTTER_ICON_SIZE = grid(3);

export interface CameraCaptureModalProps {
  /**
   * The finished photo, and the whole of what this modal produces.
   *
   * A `File` and nothing else: the caller decides what a photo is for. In the Ask composer and in
   * Capture that means handing it to the same staging path a dropped file takes, so the content-type
   * allowlist, the size cap and the free-plan rule are applied to a photo exactly once and in
   * exactly the place they are applied to everything else. A file field would hand it to its own
   * uploader for the same reason.
   */
  onCapture: (file: File) => void;
  onClose: () => void;
  /**
   * Which z-index band the viewfinder opens in, and the one thing a caller has to get right.
   *
   * `'standard'` for anything opened from the page — a field, a form, an ordinary modal — which is
   * why it is the default. `'floating'` only when the surface that opens this is itself floating:
   * standard modals share one band, so a standard viewfinder opened from a floating panel renders
   * *behind* the panel that opened it. Floating puts it in the same band, where DOM order — this
   * opens last — puts it on top. The cost is that floating does not inert what is behind it, so
   * global hotkeys are turned off by hand below.
   */
  mode?: 'standard' | 'floating';
}

/**
 * The viewfinder: opens a camera, takes one photo, and lets it be looked at before it is kept.
 *
 * The review step is the point. A recording can be judged by its length, but a photo of a delivery
 * note is either readable or it is not, and finding that out from a thumbnail in the tray — after
 * the upload, with the modal gone — means starting over. So the shutter stops the camera and shows
 * the still, and nothing leaves here until the user says it can.
 *
 * Knows nothing about what the photo is for. It opens a camera, hands back one `File`, and leaves
 * every question of allowed types, size caps and where the bytes go to whoever opened it — which is
 * what lets the Ask composer, Capture and a file field share one viewfinder rather than each grow
 * their own. The only thing a caller has to think about is `mode`.
 */
export function CameraCaptureModal({
  onCapture,
  onClose,
  mode = 'standard',
}: CameraCaptureModalProps) {
  const { status, stream, deviceLabel, deviceId, start, stop } = useCameraStream();
  const { devices, selectedDeviceId, loadDevices, selectDevice, forgetDevice } = useCameraDevices();

  const videoRef = useRef<HTMLVideoElement>(null);
  /** The photo taken, and the `blob:` URL showing it. Undefined while the viewfinder is live. */
  const [photo, setPhoto] = useState<{ file: File; url: string } | undefined>(undefined);
  const [didCaptureFail, setDidCaptureFail] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Floating surfaces leave the page live, which normally is the point — but a viewfinder is a
  // modal moment, and a stray keystroke reaching the workspace behind it is not what the user meant.
  // A standard modal already inerts what is behind it, so this is only needed for the other one.
  useDisableGlobalHotkeys(mode === 'floating');

  // Opened once, on the way in, with whatever camera was used last. Held in a ref rather than in the
  // dependency list because choosing a different camera changes `selectedDeviceId`, and this must
  // not reopen when it does — `handleSwitch` has already opened the one that was chosen.
  const selectedDeviceIdRef = useRef(selectedDeviceId);
  selectedDeviceIdRef.current = selectedDeviceId;
  const hasOpenedRef = useRef(false);
  useEffect(() => {
    if (hasOpenedRef.current) {
      return;
    }
    hasOpenedRef.current = true;
    void start(selectedDeviceIdRef.current);
  }, [start]);

  // The list is read once the camera is live and not before: names and ids are withheld until the
  // permission is in, so enumerating any earlier returns a list with nothing in it worth showing.
  useEffect(() => {
    if (status === 'live') {
      void loadDevices();
    }
  }, [status, loadDevices]);

  // The remembered camera has been unplugged since it was chosen. Recoverable without asking:
  // forget it and open whatever the browser offers, which is what the user would have picked
  // anyway. This cannot loop — a request with no id can never come back `device-missing`.
  useEffect(() => {
    if (status !== 'device-missing') {
      return;
    }
    forgetDevice();
    void start();
  }, [status, forgetDevice, start]);

  // The stream is attached imperatively; there is no `srcObject` attribute to render.
  useEffect(() => {
    const video = videoRef.current;
    if (isNil(video)) {
      return;
    }
    video.srcObject = stream ?? null;
  }, [stream]);

  // The still is only ever shown by this modal, so its URL dies with it — and with the previous
  // photo when a retake replaces it. The caller gets the `File`, and whatever stages that makes a
  // URL of its own.
  useEffect(() => {
    if (isNil(photo)) {
      return;
    }
    return () => URL.revokeObjectURL(photo.url);
  }, [photo]);

  const handleShutter = useCallback(async () => {
    const video = videoRef.current;
    if (isNil(video)) {
      return;
    }
    const file = await capturePhotoFile(video);
    if (isNil(file)) {
      setDidCaptureFail(true);
      return;
    }
    setDidCaptureFail(false);
    setPhoto({ file, url: URL.createObjectURL(file) });
    // The camera closes the moment the photo exists, so the indicator light goes out while the user
    // is deciding rather than staying lit over a still image.
    stop();
  }, [stop]);

  const handleRetake = useCallback(() => {
    setPhoto(undefined);
    setDidCaptureFail(false);
    void start(selectedDeviceId);
  }, [start, selectedDeviceId]);

  const handleUse = useCallback(() => {
    if (isNil(photo)) {
      return;
    }
    onCapture(photo.file);
    onClose();
  }, [photo, onCapture, onClose]);

  const handleSwitch = useCallback(
    async (device: CameraDevice) => {
      setIsPickerOpen(false);
      selectDevice(device.deviceId);
      await start(device.deviceId);
    },
    [selectDevice, start],
  );

  /**
   * One `anchor-name` on the switcher, shared by its menu and its tooltip — see the comment where
   * they are rendered. Named here because a hook cannot be called inside the branch that uses it.
   */
  const anchorName = getAnchorName('camera-switch', useId());

  const isReviewing = !isNil(photo);
  const notice = isReviewing ? '' : viewfinderNotice(status, didCaptureFail);
  // Only where there is something to switch between. One camera and a switcher is a control that
  // reopens the camera already open.
  const canSwitch = devices.length > 1;
  const switchLabel = i18n('camera.switch');

  return (
    <Modal
      isOpen
      mode={mode}
      pageWidth
      onOpenChange={onClose}
      // `.dialog` sets `min-height: 30vh` on the bare class, which a consumer className only beats
      // by source order. The same inline escape hatch `AskPanel` and `CaptureModal` use.
      style={{ minHeight: 0 }}
      className={styles.cameraModal}
      testID="camera-modal"
      aria-label={i18n('camera.title')}
    >
      <Modal.TitleBar
        title={i18n('camera.title')}
        startContent={<Modal.CloseButton onClick={onClose} testID="camera-close" />}
      />

      <div className={styles.stage}>
        {isReviewing ? (
          <img src={photo.url} alt={i18n('camera.photoAlt')} className={styles.frame} />
        ) : (
          // `playsInline` keeps iOS Safari from taking the preview full-screen the moment it plays;
          // `muted` is what lets it autoplay at all. There is no audio track to mute either way.
          <video
            ref={videoRef}
            className={styles.frame}
            autoPlay
            playsInline
            muted
            aria-label={i18n('camera.viewfinderLabel')}
            data-testid="camera-viewfinder"
          />
        )}
        {/* Mounted for the life of the modal rather than inserted with its text: a live region that
            arrives already populated is routinely dropped, which is exactly the moment a refused
            camera needs announcing. */}
        <p
          className={styles.notice}
          role="status"
          data-empty={isEmpty(notice) || undefined}
          data-testid="camera-notice"
        >
          {notice}
        </p>
      </div>

      <div className={styles.controls}>
        {isReviewing ? (
          <>
            <Button
              variant="ghost"
              size="large"
              icon="arrow-refresh"
              label={i18n('camera.retake')}
              onClick={handleRetake}
              testID="camera-retake"
            />
            <Button
              variant="primary"
              size="large"
              label={i18n('camera.use')}
              onClick={handleUse}
              testID="camera-use"
            />
          </>
        ) : (
          <>
            {canSwitch && (
              <Menu
                isOpen={isPickerOpen}
                onOpenChange={setIsPickerOpen}
                align="left"
                anchorName={anchorName}
              >
                {/* The tooltip sets `anchor-name` on its own child in an effect, overwriting
                    anything React put there, so the menu's name is handed *to* the tooltip rather
                    than applied alongside it. Learned the hard way in `AskRecordButton`. */}
                <AnchoredTooltip tooltip={switchLabel} position="top" anchorName={anchorName}>
                  <Button
                    variant="ghost"
                    size="large"
                    icon="arrows-swap"
                    iconOnly
                    iconSize={grid(2.5)}
                    label={switchLabel}
                    onClick={() => setIsPickerOpen(true)}
                    testID="camera-switch"
                  />
                </AnchoredTooltip>
                <Menu.Content testID="camera-options">
                  <Menu.Section title={i18n('camera.cameraTitle')} />
                  <Menu.Options
                    options={devices}
                    getItemKey={device => device.deviceId}
                    getItemLabel={device => device.label}
                    // Against the camera actually granted, not the one asked for: those are not
                    // always the same, and the tick has to say which one is on screen.
                    isItemSelected={device => device.deviceId === deviceId}
                    onSelect={handleSwitch}
                  />
                </Menu.Content>
              </Menu>
            )}
            <Button
              variant="primary"
              size="large"
              icon="camera"
              iconSize={SHUTTER_ICON_SIZE}
              label={i18n('camera.take')}
              disabled={status !== 'live'}
              onClick={handleShutter}
              testID="camera-shutter"
            />
          </>
        )}
      </div>

      {/* Which camera is on screen, under the controls rather than over the picture. Only once it is
          live and only when there is a choice to have got wrong. */}
      {!isReviewing && canSwitch && !isEmpty(deviceLabel) && (
        <p className={styles.device} data-testid="camera-device">
          {deviceLabel}
        </p>
      )}
    </Modal>
  );
}

/**
 * What the viewfinder says over the picture, and nothing when the picture speaks for itself.
 *
 * Exhaustive over `CameraStatus` rather than defaulted, so a new status is a type error rather than
 * a blank rectangle with no explanation on it.
 */
function viewfinderNotice(status: CameraStatus, didCaptureFail: boolean): string {
  if (didCaptureFail) {
    return i18n('camera.captureFailed');
  }
  switch (status) {
    case 'requesting':
    // The remembered camera has gone and the default is already opening in its place. That takes
    // the same moment and deserves the same sentence — naming the swap would explain a decision the
    // user did not make and cannot act on.
    case 'device-missing':
      return i18n('camera.starting');
    case 'denied':
      return i18n('camera.denied');
    case 'no-camera':
      return i18n('camera.noCamera');
    case 'unsupported':
      return i18n('camera.unsupported');
    case 'idle':
    case 'live':
      return '';
  }
}
